import { useState } from 'react'
import { AlertTriangle, Info, Plus } from 'lucide-react'
import { Link } from 'react-router'
import {
  EmptyState, ErrorState, FormDialog, PageHeader, StatusBadge,
} from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { useLookup } from '@/hooks/useLookup'
import { toApiProblem } from '@/lib/api/client'
import { useRequisitionMutations, useRequisitions } from '../api'
import { REQUISITION_STATUS_LABELS } from '../types'
import type { RequisitionStatus } from '../types'
import { Select } from '@/components/ui/select'

const STATUS_TONE: Record<RequisitionStatus, 'neutral' | 'info' | 'success' | 'warning' | 'danger'> = {
  DRAFT: 'neutral',
  SUBMITTED: 'warning',
  APPROVED: 'success',
  REJECTED: 'danger',
  ON_HOLD: 'warning',
  FILLED: 'info',
  CANCELLED: 'neutral',
}

/**
 * Requests to hire.
 *
 * The budget column is the point of this screen. It shows one of three things and never
 * conflates them: within budget, over by N, or "nothing was checked" — because no budget
 * exists for that department and year, which is not the same as being within one.
 */
export function RequisitionsPage() {
  useBreadcrumb()
  const requisitions = useRequisitions()
  const entities = useLookup<{ id: string; name: string }>('legalEntities')
  const departments = useLookup<{ id: string; name: string }>('departments')
  const { raise } = useRequisitionMutations()

  const [draft, setDraft] = useState<{
    legalEntityId: string; departmentId: string; title: string; headcount: string
    fiscalYear: string; salaryRangeMin: string; salaryRangeMax: string; currency: string
    justification: string
  } | null>(null)
  const [error, setError] = useState<string | null>(null)

  return (
    <div className="space-y-4">
      <PageHeader
        title="Requisitions"
        subtitle="What is being hired for, and what the headcount budget said about it."
        actions={
          <Button
            type="button"
            onClick={() => {
              setError(null)
              setDraft({
                legalEntityId: entities.data?.[0]?.id ?? '', departmentId: '', title: '',
                headcount: '1', fiscalYear: String(new Date().getFullYear()),
                salaryRangeMin: '', salaryRangeMax: '', currency: 'AED', justification: '',
              })
            }}
          >
            <Plus className="size-4" aria-hidden="true" />
            New requisition
          </Button>
        }
      />

      <Card>
        {requisitions.isPending && <Skeleton className="m-4 h-24" />}
        {requisitions.isError && (
          <ErrorState error={requisitions.error} onRetry={() => void requisitions.refetch()} />
        )}
        {requisitions.isSuccess && requisitions.data.length === 0 && (
          <EmptyState
            title="Nothing is being recruited for"
            description="A requisition is raised, checked against the department's headcount budget, approved by somebody else, and then opened for applications."
          />
        )}
        {requisitions.isSuccess && requisitions.data.length > 0 && (
          <div className="overflow-x-auto overscroll-x-contain">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200 text-slate-600">
                <tr>
                  <th className="p-3 text-start font-medium">Reference</th>
                  <th className="p-3 text-start font-medium">Role</th>
                  <th className="p-3 text-start font-medium">Department</th>
                  <th className="p-3 text-end font-medium">Heads</th>
                  <th className="p-3 text-start font-medium">Against budget</th>
                  <th className="p-3 text-end font-medium">Applicants</th>
                  <th className="p-3 text-start font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {requisitions.data.map((requisition) => (
                  <tr key={requisition.id} className="border-b border-slate-100 align-top">
                    <td className="p-3">
                      <Link
                        className="font-mono underline underline-offset-2"
                        to={`/recruitment/requisitions/${requisition.id}`}
                      >
                        {requisition.reference}
                      </Link>
                    </td>
                    <td className="p-3">{requisition.title}</td>
                    <td className="p-3 text-slate-600">
                      {requisition.departmentName ?? '—'}
                    </td>
                    <td className="p-3 text-end">{requisition.headcount}</td>
                    <td className="p-3">
                      {!requisition.budgetChecked ? (
                        <span className="inline-flex items-start gap-1 text-slate-600">
                          <Info className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
                          <span className="max-w-xs text-xs">{requisition.budgetNote}</span>
                        </span>
                      ) : (requisition.budgetOverage ?? 0) > 0 ? (
                        <span className="inline-flex items-start gap-1 text-amber-700">
                          <AlertTriangle className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
                          <span className="max-w-xs text-xs">
                            {requisition.budgetOverage} over budget
                          </span>
                        </span>
                      ) : (
                        <span className="text-xs text-slate-600">
                          Within budget ({requisition.budgetFilledHeads} of{' '}
                          {requisition.budgetApprovedHeads} filled)
                        </span>
                      )}
                    </td>
                    <td className="p-3 text-end">{requisition.applicationCount}</td>
                    <td className="p-3">
                      <StatusBadge
                        status={requisition.status}
                        tone={STATUS_TONE[requisition.status as RequisitionStatus] ?? 'neutral'}
                        label={REQUISITION_STATUS_LABELS[
                          requisition.status as RequisitionStatus] ?? requisition.status}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <p className="flex items-start gap-2 text-sm text-slate-600">
        <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
        The headcount budget warns; it does not refuse. A business that needs the head has to
        be able to ask for it, and the approver — who can see the budget, the filled count and
        the overage — is the control.
      </p>

      <FormDialog
        open={draft != null}
        onOpenChange={(open) => { if (!open) setDraft(null) }}
        size="lg"
        title="Raise a requisition"
        description="The department's headcount budget is checked when this is saved, and again when it is submitted."
        submitLabel="Save as draft"
        loading={raise.isPending}
        error={error}
        onSubmit={() => {
          if (!draft) return
          void (async () => {
            setError(null)
            try {
              await raise.mutateAsync({
                legalEntityId: draft.legalEntityId,
                departmentId: draft.departmentId,
                title: draft.title,
                headcount: Number(draft.headcount),
                fiscalYear: draft.fiscalYear ? Number(draft.fiscalYear) : null,
                salaryRangeMin: draft.salaryRangeMin ? Number(draft.salaryRangeMin) : null,
                salaryRangeMax: draft.salaryRangeMax ? Number(draft.salaryRangeMax) : null,
                currency: draft.currency || null,
                justification: draft.justification || null,
              })
              setDraft(null)
            } catch (caught) {
              setError(toApiProblem(caught)?.detail ?? 'That requisition could not be saved')
            }
          })()
        }}
      >
        {draft && (
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor="req-entity">Legal entity</Label>
              <Select
                id="req-entity"
                className="w-full"
                value={draft.legalEntityId}
                onChange={(event) => setDraft({ ...draft, legalEntityId: event.target.value })}
              >
                <option value="">Choose an entity…</option>
                {entities.data?.map((entity) => (
                  <option key={entity.id} value={entity.id}>{entity.name}</option>
                ))}
              </Select>
            </div>
            <div>
              <Label htmlFor="req-department">Department</Label>
              <Select
                id="req-department"
                className="w-full"
                value={draft.departmentId}
                onChange={(event) => setDraft({ ...draft, departmentId: event.target.value })}
              >
                <option value="">Choose a department…</option>
                {departments.data?.map((department) => (
                  <option key={department.id} value={department.id}>{department.name}</option>
                ))}
              </Select>
            </div>
            <div>
              <Label htmlFor="req-title">Role</Label>
              <Input
                id="req-title" value={draft.title}
                onChange={(event) => setDraft({ ...draft, title: event.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="req-headcount">How many</Label>
              <Input
                id="req-headcount" type="number" min={1} value={draft.headcount}
                onChange={(event) => setDraft({ ...draft, headcount: event.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="req-year">Fiscal year</Label>
              <Input
                id="req-year" type="number" value={draft.fiscalYear}
                onChange={(event) => setDraft({ ...draft, fiscalYear: event.target.value })}
              />
              <p className="mt-1 text-xs text-slate-600">
                Which year's headcount budget this counts against.
              </p>
            </div>
            <div>
              <Label htmlFor="req-currency">Currency</Label>
              <Input
                id="req-currency" maxLength={3} value={draft.currency}
                onChange={(event) =>
                  setDraft({ ...draft, currency: event.target.value.toUpperCase() })}
              />
            </div>
            <div>
              <Label htmlFor="req-min">Salary range from</Label>
              <Input
                id="req-min" type="number" value={draft.salaryRangeMin}
                onChange={(event) => setDraft({ ...draft, salaryRangeMin: event.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="req-max">Salary range to</Label>
              <Input
                id="req-max" type="number" value={draft.salaryRangeMax}
                onChange={(event) => setDraft({ ...draft, salaryRangeMax: event.target.value })}
              />
              <p className="mt-1 text-xs text-slate-600">
                Offers outside the range are flagged for the approver, not refused.
              </p>
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="req-justification">Why this role is needed</Label>
              <Input
                id="req-justification" value={draft.justification}
                onChange={(event) => setDraft({ ...draft, justification: event.target.value })}
              />
            </div>
          </div>
        )}
      </FormDialog>
    </div>
  )
}
